import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaBars, FaTimes, FaPhone, FaEnvelope, FaChevronDown } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import './Navbar.css';
import { siteConfig } from '../data/siteData';

const serviceLinks = [
  {
    label: 'Residential Construction',
    path: '/services/design/residential',
    desc: 'Villas, apartments & remodeling',
  },
  {
    label: 'Commercial Construction',
    path: '/services/design/commercial',
    desc: 'Offices, retail & industrial',
  },
  {
    label: 'Interior Design',
    path: '/services/design/interior',
    desc: 'Home, office & space planning',
  },
  {
    label: 'Designs',
    path: '/services/design/designs',
    desc: 'Floor plans, 2D & 3D renders',
  },
  {
    label: 'Contact Us',
    path: '/contact',
    desc: 'Get a free consultation today',
  },
];

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen]         = useState(false);
  const [isScrolled, setIsScrolled]         = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setIsServicesOpen(false);
  }, [location]);

  return (
    <>
      {/* TOP BAR */}
      <div className="top-bar">
        <div className="container">
          <div className="top-bar-content">
            <div className="contact-info">
              <a href={`tel:${siteConfig.phone}`} className="top-bar-link">
                <FaPhone /> {siteConfig.phone}
              </a>
              <a href={`mailto:${siteConfig.email}`} className="top-bar-link">
                <FaEnvelope /> {siteConfig.email}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* NAVBAR */}
      <nav className={`navbar ${isScrolled ? 'navbar-scrolled' : ''}`}>
        <div className="container">
          <div className="navbar-content">

            {/* LOGO */}
            <Link to="/" className="navbar-logo">
              <span className="logo-text">{siteConfig.companyName}</span>
            </Link>

            {/* DESKTOP MENU */}
            <ul className="nav-links desktop-menu">
              <li>
                <Link to="/" className={location.pathname === '/' ? 'active' : ''}>Home</Link>
              </li>
              <li>
                <Link to="/about" className={location.pathname === '/about' ? 'active' : ''}>About</Link>
              </li>

              {/* SERVICES DROPDOWN */}
              <li
                className="services-dropdown"
                onMouseEnter={() => setIsServicesOpen(true)}
                onMouseLeave={() => setIsServicesOpen(false)}
              >
                <span className={`services-link ${isServicesOpen ? 'services-link-active' : ''}`}>
                  Services <FaChevronDown className={`dropdown-icon ${isServicesOpen ? 'rotated' : ''}`} />
                </span>

                <AnimatePresence>
                  {isServicesOpen && (
                    <motion.div
                      className="services-menu"
                      initial={{ opacity: 0, y: 14, scale: 0.97 }}
                      animate={{ opacity: 1, y: 0,  scale: 1    }}
                      exit={{    opacity: 0, y: 14, scale: 0.97 }}
                      transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                    >
                      <div className="dropdown-header">
                        <span className="dropdown-header-label">OUR SERVICES</span>
                        <div className="dropdown-header-line" />
                      </div>

                      <ul className="dropdown-list">
                        {serviceLinks.map((s, i) => (
                          <motion.li
                            key={s.path}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.06, duration: 0.18 }}
                            className={s.label === 'Contact Us' ? 'dropdown-contact-item' : ''}
                          >
                            <Link to={s.path} className="dropdown-item">
                              <span className="dropdown-item-text">
                                <span className="dropdown-item-label">{s.label}</span>
                                <span className="dropdown-item-desc">{s.desc}</span>
                              </span>
                              <span className="dropdown-item-arrow">→</span>
                            </Link>
                          </motion.li>
                        ))}
                      </ul>
                    </motion.div>
                  )}
                </AnimatePresence>
              </li>

              <li>
                <Link to="/projects" className={location.pathname === '/projects' ? 'active' : ''}>Projects</Link>
              </li>

              {/* ── GALLERY ── */}
              <li>
                <Link to="/gallery" className={location.pathname === '/gallery' ? 'active' : ''}>Gallery</Link>
              </li>

              <li>
                <Link to="/contact" className={location.pathname === '/contact' ? 'active' : ''}>Contact</Link>
              </li>
            </ul>

            {/* MOBILE TOGGLE */}
            <button
              className="mobile-menu-toggle"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <FaTimes /> : <FaBars />}
            </button>

          </div>
        </div>

        {/* MOBILE MENU */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              className="mobile-menu"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{    opacity: 0, height: 0 }}
              transition={{ duration: 0.28 }}
            >
              <ul className="mobile-nav-links">
                <li><Link to="/" className={location.pathname === '/' ? 'active' : ''}>Home</Link></li>
                <li><Link to="/about" className={location.pathname === '/about' ? 'active' : ''}>About</Link></li>

                <li className="mobile-services">
                  <span onClick={() => setIsServicesOpen(!isServicesOpen)}>
                    Services <FaChevronDown className={isServicesOpen ? 'rotated' : ''} />
                  </span>
                  {isServicesOpen && (
                    <ul className="mobile-submenu">
                      {serviceLinks.map((s) => (
                        <li key={s.path}>
                          <Link to={s.path}>{s.label}</Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>

                <li><Link to="/projects" className={location.pathname === '/projects' ? 'active' : ''}>Projects</Link></li>

                {/* ── GALLERY ── */}
                <li><Link to="/gallery" className={location.pathname === '/gallery' ? 'active' : ''}>Gallery</Link></li>

                <li><Link to="/contact" className={location.pathname === '/contact' ? 'active' : ''}>Contact</Link></li>
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </>
  );
};

export default Navbar;